/**
 * Link do porównania ofert „Sprawdź ofertę” (prośba właściciela 2026-09-29).
 *
 * Cały stan ofert (cena, sprzęt, liczby sztuk, dach, dodatki) idzie do adresu po `#o=`,
 * zakodowany jako JSON w base64url. Po `#`, a nie w parametrze zapytania, bo część po `#`
 * nie trafia do serwera: ceny z cudzych ofert nie lądują w logach nginx.
 *
 * Link niesie tylko odpowiedzi z formularza. Nazwy i zdjęcia modeli dociągamy po otwarciu
 * (`GET /api/sprawdz_oferte/model`), a raport liczymy od nowa, z aktualnych cen. Dzięki
 * temu link jest krótki i nie przenosi nieaktualnych kwot.
 *
 * Dane z adresu są NIEZAUFANE: każde pole jest przycinane do spodziewanego typu i długości,
 * a i tak trafiają tylko do formularza i do naszego API, które je waliduje.
 */

export const WERSJA_LINKU = 1
const MAKS_OFERT = 4
const TRYBY = ['brutto', 'netto']
const DACHY = ['blacha', 'dachowka', 'grunt']
const ODPOWIEDZI = ['tak', 'nie', '?']
const FAZY = ['1', '3', '?']

const tekst = (v, max = 80) => (typeof v === 'string' ? v.slice(0, max) : '')
export const liczba = (v, max) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 && n <= max ? n : null
}
const zListy = (v, lista, domyslna) => (lista.includes(v) ? v : domyslna)

export const base64url = (s) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
export const zBase64url = (s) => {
  const b = s.replace(/-/g, '+').replace(/_/g, '/')
  return decodeURIComponent(escape(atob(b + '='.repeat((4 - (b.length % 4)) % 4))))
}

/** Wybór sprzętu w wersji do linku: tylko to, czego nie da się dociągnąć z katalogu. */
export const skrocWybor = (w) => {
  if (!w) return null
  const s = {}
  if (w.model?.id) s.i = w.model.id
  if (w.producent) s.p = w.producent
  if (w.seria) s.s = w.seria
  if (w.moc !== '' && w.moc != null) s.w = w.moc
  return Object.keys(s).length ? s : null
}

export const rozwinWybor = (s) => {
  if (!s || typeof s !== 'object') return null
  const w = { producent: tekst(s.p), seria: tekst(s.s), moc: liczba(s.w, 100000) ?? '' }
  const id = liczba(s.i, 1e9)
  if (id && Number.isInteger(id)) w.model = { id }
  return w.producent || w.moc || w.model ? w : null
}

/** Oferty (stan formularza) i indeks aktywnej -> fragment adresu `o=...`. */
export const zakodujOferty = (oferty, aktywna = 0) => {
  const o = oferty.slice(0, MAKS_OFERT).map((x) => ({
    c: [x.cena?.kwota ?? '', x.cena?.tryb, x.cena?.vat],
    f: skrocWybor(x.falownik),
    fz: x.fazy,
    m: x.magazyn?.brak === true ? 0
      : x.magazyn?.brak === false ? { s: skrocWybor(x.magazyn.wybor), n: x.magazyn.sztuk || 1 } : null,
    p: skrocWybor(x.moduly),
    n: x.liczba,
    d: x.dach,
    z: x.zasilanie,
    o: x.optymalizatory,
    // Rozbudowa o magazyn: tylko gdy włączona, żeby stare linki i zwykłe oferty się nie zmieniły.
    ...(x.rozbudowa ? { r: 1 } : {}),
  }))
  return `o=${base64url(JSON.stringify({ v: WERSJA_LINKU, a: aktywna, o }))}`
}

/**
 * Fragment adresu (z `#` albo bez) -> { oferty, aktywna } albo null, gdy link jest uszkodzony.
 * Oferty mają kształt stanu formularza; modele mają samo `id` (reszta do dociągnięcia).
 */
export const odkodujOferty = (fragment) => {
  const m = String(fragment || '').replace(/^#/, '').match(/(?:^|&)o=([A-Za-z0-9_-]+)/)
  if (!m) return null
  let d
  try {
    d = JSON.parse(zBase64url(m[1]))
  } catch {
    return null
  }
  if (!d || d.v !== WERSJA_LINKU || !Array.isArray(d.o) || !d.o.length) return null
  const oferty = d.o.slice(0, MAKS_OFERT).map((x) => {
    const c = Array.isArray(x.c) ? x.c : []
    const mag = x.m === 0 ? { brak: true, wybor: null, sztuk: 1 }
      : x.m && typeof x.m === 'object'
        ? { brak: false, wybor: rozwinWybor(x.m.s), sztuk: liczba(x.m.n, 50) && Number.isInteger(Number(x.m.n)) ? Number(x.m.n) : 1 }
        : { brak: null, wybor: null, sztuk: 1 }
    const kwota = liczba(c[0], 10000000)
    const sztukModulow = liczba(x.n, 500)
    return {
      cena: { kwota: kwota ? String(kwota) : '', tryb: zListy(c[1], TRYBY, 'brutto'), vat: [8, 23].includes(Number(c[2])) ? Number(c[2]) : 8 },
      falownik: rozwinWybor(x.f),
      fazy: zListy(String(x.fz ?? '?'), FAZY, '?'),
      magazyn: mag,
      moduly: rozwinWybor(x.p),
      liczba: sztukModulow ? String(Math.round(sztukModulow)) : '',
      dach: zListy(x.d, DACHY, 'blacha'),
      zasilanie: zListy(x.z, ODPOWIEDZI, '?'),
      optymalizatory: zListy(x.o, ODPOWIEDZI, 'nie'),
      rozbudowa: x.r === 1,
      wynik: null,
    }
  })
  const a = Number(d.a)
  return { oferty, aktywna: Number.isInteger(a) && a >= 0 && a < oferty.length ? a : 0 }
}

/**
 * Wejście do „Sprawdź ofertę” ze strony modelu (kafelek „Oferta pod lupą”, 2026-10-08): w formularz
 * trafia tylko ten jeden model, bez liczenia raportu. Osobny znacznik zamiast `#o=`, bo link oferty
 * bez ceny i reszty sprzętu kończyłby się komunikatem, że raportu nie da się policzyć.
 */
const KATEGORIE_SPRZETU = { inwerter: 'falownik', panel: 'moduly', magazyn: 'magazyn' }

export const linkDoSprawdzeniaOferty = (kategoriaModelu, id) => {
  const k = KATEGORIE_SPRZETU[kategoriaModelu]
  return k && Number.isInteger(Number(id)) && Number(id) > 0 ? `/sprawdz-oferte#sprzet=${k}-${Number(id)}` : '/sprawdz-oferte'
}

/** `#sprzet=falownik-123` -> { sekcja: 'falownik' | 'moduly' | 'magazyn', id } albo null. */
export const odczytajSprzetZLinku = (fragment) => {
  const m = String(fragment || '').match(/(?:^#|&)sprzet=(falownik|moduly|magazyn)-(\d{1,9})(?:&|$)/)
  return m ? { sekcja: m[1], id: Number(m[2]) } : null
}
